<script setup lang="ts">
import { onErrorCaptured, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { ElConfigProvider } from 'element-plus'
import { Alert } from '@/shared/ui/Alert'
import { Button } from '@/shared/ui/Button'
import { Inline } from '@/shared/ui/Inline'
import { Stack } from '@/shared/ui/Stack'
import { useViewport } from '@/shared/lib'

const { isMobileViewport } = useViewport()

/**
 * The page shell, and **the FSD tree's only error boundary**. The signed-in shell renders
 * it once around the router view (`apps/adok/app/layout/ui/AppMain.vue`), so a page does
 * not write it and needs no error handling of its own; a page drawn outside that shell
 * (404) wraps itself in it. A throw during render draws a message and a "Try again"
 * instead of blanking the page, and the boundary clears itself on the next route.
 *
 * **It does not swallow the error.** Returning `false` would stop propagation, and Sentry
 * is the app-level handler, so the report would stop with it; nothing is returned here.
 *
 * What it cannot cover, deliberately: a throw in the legacy shell around it (sidebar,
 * navbar), and anything Vue never sees, such as an unhandled rejection outside a lifecycle.
 * Both would need the boundary one layer up, in `src/layout`, which traforama also renders.
 */
const failed = ref(false)

onErrorCaptured((error) => {
  failed.value = true
  // The publisher gets a sentence, not a stack trace; this is the part a developer needs.
  console.error(error)
})

const route = useRoute()

// A modal route reuses the page component, so without this a failure inside a dialog would
// outlive the dialog and leave the page stuck with no way back but a reload. Re-rendering a
// page that still throws simply lands here again.
watch(
  () => route.fullPath,
  () => {
    failed.value = false
  }
)
</script>

<template>
  <div class="base-container">
    <ElConfigProvider size="small">
      <el-main class="shadow" :class="{ 'p-none': isMobileViewport }">
        <Stack v-if="failed">
          <Alert type="error" :closable="false" show-icon>
            Something went wrong while drawing this page. Nothing you did is
            lost — reloading usually fixes it, and the failure has been
            reported.
          </Alert>

          <Inline>
            <Button type="primary" @click="$router.go(0)">Reload</Button>
          </Inline>
        </Stack>

        <slot v-else />
      </el-main>
    </ElConfigProvider>
  </div>
</template>

<style lang="scss" scoped>
.base-container {
  min-height: 105vh;
  background: var(--el-fill-color-light, #f5f5f5);
}
</style>
